import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadState, saveState } from "../../stores/store";
import { generateInvoicePDF } from "../../utils/invoiceUtils";
import {
  Card,
  Col,
  Space,
  Row,
  Button,
  Skeleton,
  List,
  Typography,
  message,
  Popover,
} from "antd";
import { DownloadOutlined, CloseOutlined } from "@ant-design/icons";
import { useSettings } from "../../contexts/settingsContext";
import { searchContestants } from "../../api/contestant";
import {
  getInvoicesByContestant,
  removeMerchFromInvoice,
  removePassFromInvoice,
} from "../../api/invoice";
import {
  getApplicationsByContestant,
  deleteApplication,
} from "../../api/application";
import { getPassesByIds } from "../../api/pass";
import PaymentButton from "../Invoices/invoiceButton";
import { markApplicationComplete } from "../../api/user";
import "./eventSummary.scss";

const EventSummary = () => {
  const navigate = useNavigate();
  const { Title, Text, Link } = Typography;
  const { state: settings } = useSettings();
  const [contestant, setContestant] = useState(null);
  const [selectedRound, setSelectedRound] = useState(
    settings?.selectedRound ?? 1
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allPaid, setAllPaid] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [applications, setApplications] = useState([]);
  const [passes, setPasses] = useState([]);
  const [merchandise, setMerchandise] = useState([]);
  const state = loadState();

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);

      try {
        // searchContestants returns: { data: [...], total: number }
        const result = await searchContestants();
        const contestants = Array.isArray(result?.data) ? result.data : [];

        if (!contestants.length) {
          setError("No contestants found");
          setContestant(null);
          setInvoice(null);
          setApplications([]);
          setPasses([]);
          setMerchandise([]);
          return;
        }

        // Simple: just take the first contestant (matches your original example)
        const fetchedContestant = contestants[0];
        setContestant(fetchedContestant);

        if (fetchedContestant && fetchedContestant._id) {
          await fetchContestantData(fetchedContestant._id, selectedRound);
        }
      } catch (err) {
        console.error("Error fetching contestant", err);
        setError("Error fetching contestant");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [selectedRound]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchContestantData = async (contestantId, round) => {
    try {
      const invoiceData = await getInvoicesByContestant(contestantId, round);

      if (!Array.isArray(invoiceData) || invoiceData.length === 0) {
        setInvoice(null);
        setAllPaid(false);
        setPasses([]);
        setMerchandise([]);
        setApplications([]);
        return;
      }

      const invoiceToUpdate = invoiceData[0];
      setInvoice(invoiceToUpdate);
      setAllPaid(!!invoiceToUpdate.paid);

      const passIds = (invoiceToUpdate.passes || []).map((p) => p.pass);
      if (passIds.length > 0) {
        const passData = await getPassesByIds(passIds);
        setPasses(passData || []);
      } else {
        setPasses([]);
      }

      const merchandiseData = (invoiceToUpdate.merchandise || []).map(
        (m) => m.product
      );
      setMerchandise(merchandiseData || []);

      const applicationsData = await getApplicationsByContestant(contestantId);
      setApplications(applicationsData || []);
    } catch (err) {
      console.error("Error fetching contestant data", err);
      setError("Error fetching contestant data");
    }
  };

  const handleRemoveApplication = async (applicationId) => {
    if (invoice?.paid) {
      message.error("Cannot remove application from a paid invoice");
      return;
    }
    try {
      await deleteApplication(applicationId);
      message.success("Application removed successfully");
      if (contestant?._id) {
        await fetchContestantData(contestant._id, selectedRound);
      }
    } catch (err) {
      console.error(err);
      message.error("Error removing application");
    }
  };

  const handleRemovePass = async (passId) => {
    if (invoice?.paid) {
      message.error("Cannot remove pass from a paid invoice");
      return;
    }
    try {
      await removePassFromInvoice(invoice._id, passId);
      message.success("Pass removed successfully");
      if (contestant?._id) {
        await fetchContestantData(contestant._id, selectedRound);
      }
    } catch (err) {
      console.error(err);
      message.error("Error removing pass");
    }
  };

  const handleRemoveMerchandise = async (productId) => {
    if (invoice?.paid) {
      message.error("Cannot remove merchandise from a paid invoice");
      return;
    }
    try {
      await removeMerchFromInvoice(invoice._id, productId);
      message.success("Merchandise removed successfully");
      if (contestant?._id) {
        await fetchContestantData(contestant._id, selectedRound);
      }
    } catch (err) {
      console.error(err);
      message.error("Error removing merchandise");
    }
  };

  const handleGeneratePDF = () => {
    if (!invoice || !contestant) {
      message.error("Invoice or contestant data missing");
      return;
    }

    const invoiceData = {
      contestantName: `${contestant.first_name} ${contestant.last_name}`,
      totalEvents: (invoice.applications || []).length,
      feeBreakdown: invoice.fee || {},
      applications: invoice.applications || [],
      passes,
      merchandise,
    };

    generateInvoicePDF(invoiceData, settings, applications);
  };

  const handlePayLater = async () => {
    try {
      await markApplicationComplete(state.user._id);
      state.user.applicationCompleted = true;
      saveState(state);
      navigate("/invoice", { replace: true });
    } catch (err) {
      console.error(err);
      message.error("Could not mark application as complete");
    }
  };

  const title = `Contestant Name: ${
    contestant ? `${contestant.first_name} ${contestant.last_name}` : ""
  }`;
  const title2 = `Contestant Number: ${contestant?.contestant_number ?? ""}`;

  return (
    <div className="event-summary-page">
      <div className="header-actions">
        <Title level={4} style={{ margin: 0 }}>
          {title}
        </Title>
        <Title level={4} style={{ margin: 0 }}>
          {title2}
        </Title>
        <Space>
          <Button
            className="generate-pdf-button"
            type="primary"
            onClick={handleGeneratePDF}
            disabled={!invoice || !contestant}
          >
            <DownloadOutlined /> Download Invoice
          </Button>
          {/* <Button onClick={handlePayLater}>Pay Later</Button> */}
        </Space>
      </div>

      <div className="content-wrapper">
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Row gutter={[16, 16]}>
            <Col span={24} md={16}>
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <Skeleton active loading={loading}>
                  {applications.filter((app) => app.event?.solo_bool).length >
                    0 && (
                    <Card title="Solo Entries" style={{ marginBottom: 16 }}>
                      <List
                        size="small"
                        dataSource={applications.filter(
                          (app) => app.event?.solo_bool
                        )}
                        renderItem={(item) => {
                          const eventName = item?.event?.name ?? "Unknown Event";
                          const eventStyle =
                            item?.event?.style ?? "Unknown Style";
                          const groupName =
                            item?.group?.group_name ?? "Unknown Group";
                          const isSolo = item?.event?.solo_bool ?? false;
                          const instrument = item?.instrument ?? "";
                          return (
                            <List.Item>
                              <List.Item.Meta
                                title={
                                  instrument
                                    ? `${eventName} - ${instrument}`
                                    : eventName
                                }
                                description={`${eventStyle} (${
                                  isSolo ? "Solo" : `Group - ${groupName}`
                                })`}
                              />
                            </List.Item>
                          );
                        }}
                      />
                    </Card>
                  )}

                  {applications.filter((app) => !app.event?.solo_bool).length >
                    0 && (
                    <Card title="Group Entries" style={{ marginBottom: 16 }}>
                      <List
                        size="small"
                        dataSource={applications.filter(
                          (app) => !app.event?.solo_bool
                        )}
                        renderItem={(item) => {
                          const eventName = item?.event?.name ?? "Unknown Event";
                          const eventStyle =
                            item?.event?.style ?? "Unknown Style";
                          const groupName =
                            item?.group?.group_name ?? "Unknown Group";
                          const isSolo = item?.event?.solo_bool ?? false;
                          return (
                            <List.Item
                              actions={
                                !invoice?.paid
                                  ? [
                                      <Button
                                        key="remove"
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={() =>
                                          handleRemoveApplication(item._id)
                                        }
                                      />,
                                    ]
                                  : null
                              }
                            >
                              <List.Item.Meta
                                title={eventName}
                                description={`${eventStyle} (${
                                  isSolo ? "Solo" : `Group - ${groupName}`
                                })`}
                              />
                            </List.Item>
                          );
                        }}
                      />
                    </Card>
                  )}

                  {passes.length > 0 && (
                    <Card title="Passes" style={{ marginBottom: 16 }}>
                      <List
                        size="small"
                        dataSource={passes}
                        renderItem={(item) => (
                          <List.Item
                            actions={
                              !invoice?.paid
                                ? [
                                    <Button
                                      key="remove"
                                      type="text"
                                      icon={<CloseOutlined />}
                                      onClick={() => handleRemovePass(item._id)}
                                    />,
                                  ]
                                : null
                            }
                          >
                            <List.Item.Meta
                              title={`${item.type} Pass`}
                              description={`Day: ${item.day} - Price: R ${item.price}`}
                            />
                          </List.Item>
                        )}
                      />
                    </Card>
                  )}

                  {merchandise.length > 0 && (
                    <Card title="Merchandise" style={{ marginBottom: 16 }}>
                      <List
                        size="small"
                        dataSource={merchandise}
                        renderItem={(item) => (
                          <List.Item
                            actions={
                              !invoice?.paid
                                ? [
                                    <Button
                                      key="remove"
                                      type="text"
                                      icon={<CloseOutlined />}
                                      onClick={() =>
                                        handleRemoveMerchandise(item._id)
                                      }
                                    />,
                                  ]
                                : null
                            }
                          >
                            <List.Item.Meta
                              title={item.description}
                              description={`Price: R ${item.price}`}
                            />
                          </List.Item>
                        )}
                      />
                    </Card>
                  )}
                </Skeleton>
              </Space>
            </Col>

            <Col span={24} md={8}>
              <Skeleton active loading={loading}>
                <Card title="Payment Details">
                  <Space
                    size="large"
                    direction="vertical"
                    style={{ width: "100%" }}
                  >
                    <List>
                      <List.Item>
                        <Text>
                          Base Fee: R {invoice?.fee?.base_fee || 0}
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          Group Fee: R {invoice?.fee?.group_fee || 0}
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text>
                          Additional Fee: R {invoice?.fee?.additional_fee || 0}{" "}
                          <Popover
                            title="Additional Fee"
                            content="If you have applied for more events than are covered by the base fee, you will be charged an additional fee per extra event."
                          >
                            <Link>What is this?</Link>
                          </Popover>
                        </Text>
                      </List.Item>
                      <List.Item>
                        <Text strong style={{ fontSize: "16px" }}>
                          Total: R {invoice?.fee?.total_fee_unpaid || 0}
                        </Text>
                      </List.Item>
                    </List>

                    <div
                      style={{
                        textAlign: "right",
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                    >
                      {invoice && (
                        <PaymentButton invoices={[invoice]} settings={settings} />
                      )}
                      {/* <Button onClick={handlePayLater}>Pay Later</Button> */}
                    </div>
                  </Space>
                </Card>
              </Skeleton>
            </Col>
          </Row>

          {error && (
            <Text type="danger" style={{ marginTop: 8 }}>
              {error}
            </Text>
          )}
        </Space>
      </div>
    </div>
  );
};

export default EventSummary;
